import React, { useCallback, useMemo, useState } from "react";

import { computeEffectiveItems } from "src/components/Select/utils";
import type { SelectOptionBase as MultiSelectOptionBase } from "src/components/Select/types";

export const CREATE_PREFIX = "__create__:";

export const isCreateValue = (val: unknown): val is string =>
  typeof val === "string" && val.startsWith(CREATE_PREFIX);

interface UseCreatableItemsConfig {
  isCreatable: boolean;
  isGrouped: boolean;
  flatOptions: MultiSelectOptionBase[];
  selectedValues: string[];
  baseUiItems?: string[] | { label: string; items: string[] }[];
  onCreateOption?: (inputValue: string) => void;
  onChange?: (values: string[]) => void;
  onAsyncInputChange?: (value: string) => void;
  children?: React.ReactNode;
}

interface UseCreatableItemsReturn {
  inputValue: string;
  showCreateOption: boolean;
  effectiveItems: string[] | { label: string; items: string[] }[] | undefined;
  creatableFilter: (
    itemValue: string,
    query: string,
    itemToString?: (v: string) => string
  ) => boolean;
  handleInputValueChange: (value: string) => void;
  handleValueChange: (next: string[] | null) => void;
}

export function useCreatableItems({
  isCreatable,
  isGrouped,
  flatOptions,
  selectedValues,
  baseUiItems,
  onCreateOption,
  onChange,
  onAsyncInputChange,
  children,
}: UseCreatableItemsConfig): UseCreatableItemsReturn {
  const [inputValue, setInputValue] = useState("");

  const handleInputValueChange = useCallback(
    (value: string) => {
      setInputValue(value);
      onAsyncInputChange?.(value);
    },
    [onAsyncInputChange]
  );

  const trimmedInput = inputValue.trim();
  const showCreateOption = useMemo(() => {
    if (!isCreatable || trimmedInput.length === 0) return false;
    const lower = trimmedInput.toLowerCase();
    return (
      !flatOptions.some(
        o => o.label.toLowerCase() === lower || o.value.toLowerCase() === lower
      ) && !selectedValues.includes(trimmedInput)
    );
  }, [isCreatable, trimmedInput, flatOptions, selectedValues]);

  const effectiveItems = React.useMemo(
    () =>
      computeEffectiveItems(
        children,
        baseUiItems,
        showCreateOption,
        isGrouped,
        trimmedInput
      ),
    [baseUiItems, children, isGrouped, showCreateOption, trimmedInput]
  );

  // Custom filter: always pass the "Create" item through
  const creatableFilter = useCallback(
    (
      itemValue: string,
      query: string,
      itemToString?: (v: string) => string
    ) => {
      if (isCreateValue(itemValue)) return true;
      const label = itemToString?.(itemValue) ?? itemValue;
      return label.toLowerCase().includes(query.toLowerCase());
    },
    []
  );

  const handleValueChange = useCallback(
    (next: string[] | null) => {
      const raw = next ?? [];

      const createEntry = raw.find(v => isCreateValue(v));
      if (createEntry) {
        const newValue = createEntry.slice(CREATE_PREFIX.length);
        const withNewValue = raw.map(v => (v === createEntry ? newValue : v));
        onCreateOption?.(newValue);
        onChange?.(withNewValue);
        return;
      }

      onChange?.(raw);
    },
    [onChange, onCreateOption]
  );

  return {
    inputValue,
    showCreateOption,
    effectiveItems,
    creatableFilter,
    handleInputValueChange,
    handleValueChange,
  };
}
